import React, { useCallback, useEffect, useState } from "react";
import { FaBell, FaTimes } from "react-icons/fa";
import { Badge, ListGroup, Overlay } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import JajotApi from "../../api/JajotApi";
import { HiOutlineMail, HiOutlineMailOpen } from "react-icons/hi";
import { useLocation } from "react-router-dom";
import jwtDecode from "jwt-decode";
import RecordModal from "./RecordModal";
import { taskHeaders } from "../../constants/CONSTANT";

const NotificationBox = ({ refreshNotification }) => {
    const [show, setShow] = useState(false);
    const [target, setTarget] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [userContactId, setUserContactId] = useState(null);
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const location = useLocation();

    const fetchNotifications = useCallback(async (contactId) => {
        if (!contactId) return;

        try {
            const response = await JajotApi.getNotificationsList(contactId);

            if (response.success) {
                const scoped = response.records.filter((item) => String(item.relatedTo) !== "null");
                console.log({scoped});
                
                const formatted = scoped.map((item) => {
                    let relatedData = null;
                    try {
                        relatedData = typeof item.relatedTo === 'string' 
                            ? JSON.parse(item.relatedTo) 
                            : item.relatedTo;
                    } catch (e) {
                        console.error('Failed to parse relatedTo:', e);
                    }

                    const rawTitle = item.title || "";
                    const pendingMatch = rawTitle.match(/Pending For.*$/i);
                    const pendingLabel = pendingMatch ? pendingMatch[0].trim() : null;
                    const displayTitle = pendingLabel ? rawTitle.replace(pendingLabel, "").trim() : rawTitle;
                    
                    return {
                        id: item.deviceRecordId,
                        name: displayTitle,
                        pendingLabel,
                        record_id: relatedData?.taskId || relatedData?.id || null,
                        record_type: relatedData?.type || null,
                        description: item.description,
                        status: item.isActive ? "active" : "inactive",
                        type: item.platform,
                        user_id: item.contactId,
                        message: item.description || item.title,
                        read: false,
                        time: new Date(item.createdDate).toLocaleString(),
                    };
                });
                setNotifications(formatted);
            } else {
                console.warn("No notifications found");
            }
        } catch (err) {
            console.error("Error fetching notifications", err);
        }
    }, []);



    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const decoded = jwtDecode(token);
            setUserContactId(decoded?.contactDetails?.Id || null);
        } catch (err) {
            console.error("Failed to decode user token", err);
        }
    }, []);

    useEffect(() => {
        fetchNotifications(userContactId);
    }, [fetchNotifications, userContactId, location.pathname, refreshNotification]);

    const handleClick = (event) => {
        setShow(!show);
        setTarget(event.target);
    };

    const unreadCount = notifications?.filter((n) => !n.read && n.status === "active").length;

    const handleNotificationClick = async (notification) => {
        if (!notification.record_id) return;
        
        try {
            const res = await JajotApi.getRecordById(notification.record_id, 'api/related-task');
            if (res?.success && res.records?.[0]) {
                setSelectedRecord(res.records[0]);
                setShowRecordModal(true);
                setShow(false);
            }
        } catch (err) {
            console.error('Error loading task:', err);
        }
    };


    return (
        <div className="position-relative d-inline-block">
            <div className="d-flex align-items-start position-relative icon-default" onClick={handleClick}>
                <FaBell size={20} className="text-dark mx-4" title="Notification" />
                {unreadCount > 0 && (
                    <Badge
                        pill
                        className="position-absolute bg-dark"
                        style={{ fontSize: "0.6rem", top: 0, right:'2em', transform: "translate(50%, -50%)" }}
                    >
                        {unreadCount}
                    </Badge>
                )}
            </div>

            <Overlay
                show={show}
                target={target}
                placement="bottom-end"
                rootClose
                onHide={() => setShow(false)}
                style={{ width: "300px" }}
            >
                {({ placement: _placement, arrowProps: _arrowProps, show: _show, popper: _popper, ...props }) => (
                    <div
                        {...props}
                        style={{
                            ...props.style,
                            width: "300px",
                            maxWidth: "100vw",
                            backgroundColor: "white",
                            borderRadius: "0.5rem",
                            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
                            zIndex: 1000,
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                            <h6 className="mb-0 fw-bold">Notifications</h6>
                            <FaTimes className="text-muted icon-default" title="Close" onClick={() => setShow(false)} />
                        </div>

                        <ListGroup
                            variant="flush"
                            style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                            {notifications.filter(n => n.status === "active").length > 0 ? (
                                notifications.filter(n => n.status === "active")?.map((notification) => (
                                    <ListGroup.Item
                                        key={notification.id}
                                        className={`py-3 ${notification.read ? "" : "bg-light"}`}
                                        style={{ cursor: notification.record_id ? 'pointer' : 'default' }}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center w-100">
                                            <div className="col">
                                                <p
                                                    className={`mb-1 ${notification.read ? "text-muted" : "fw-bold"}`}
                                                >
                                                    {notification.name}
                                                    {notification.pendingLabel && (
                                                        <sup className="ms-1" style={{ fontSize: ".8rem" }}>
                                                            <Badge bg="warning" text="dark" className="px-1">
                                                                {notification.pendingLabel}
                                                            </Badge>
                                                        </sup>
                                                    )}
                                                </p>
                                                {notification.description && (
                                                    <p
                                                        className="mb-1 text-muted"
                                                        style={{
                                                            fontSize: "0.85rem",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "240px",
                                                        }}
                                                        title={notification.description}
                                                    >
                                                        {notification.description}
                                                    </p>
                                                )}
                                                <small className="text-muted">{notification.time}</small>
                                            </div>

                                            <div className="col-auto d-flex align-items-center">
                                                {notification.read ? (
                                                    <HiOutlineMailOpen className="fs-5" />
                                                ) : (
                                                    <HiOutlineMail className="fs-5" />
                                                )}
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                ))
                            ) : (
                                <ListGroup.Item className="text-center py-4 text-muted">
                                    No new notifications
                                </ListGroup.Item>
                            )}
                        </ListGroup>
                    </div>
                )}
            </Overlay>

            <RecordModal
                show={showRecordModal}
                onHide={() => setShowRecordModal(false)}
                record={selectedRecord}
                header={taskHeaders}
                defaultType="tasks"
            />
        </div>
    );
};

export default NotificationBox;
